import { IPassengerShifts } from 'src/types/manualOrders/api.types';
// Type only: erased at runtime, so the store that EmployeeColHeaderCell imports is not pulled in
import type { Options } from './EmployeeColHeaderCell';

// The values of Options in EmployeeColHeaderCell, as strings so this file stays free of store imports and
// can be unit-tested. The type ties each one to its enum member: if an Options value changes, this fails to compile.
const SORT: { [K in Exclude<keyof typeof Options, 'DepartmentDesc'>]: `${typeof Options[K]}` } = {
    CityAsc: 'CITY_ASC',
    CityDesc: 'CITY_DESC',
    NameAsc: 'NAME_ASC',
    NameDesc: 'NAME_DESC',
    DepartmentAsc: 'DEPARTMENT_ASC',
    CodeAsc: 'CODE_ASC',
    CodeDesc: 'CODE_DESC',
} as const;

const NUMERIC_CODE = /^\d+$/;

/**
 * Compares two passenger codes: numerically when both are all digits (2 before 10), otherwise as text
 * (numeric-aware). Empty codes are not compared here, callers keep them last.
 */
const compareCodes = (a: string, b: string): number => {
    if (NUMERIC_CODE.test(a) && NUMERIC_CODE.test(b)) {
        const diff = Number(a) - Number(b);
        if (diff !== 0) return diff < 0 ? -1 : 1;
        return a.localeCompare(b);
    }
    return a.localeCompare(b, undefined, { numeric: true });
};

/** Sorts by internalPassCode; passengers without a code always go last, in both directions */
const compareByCode = (a: IPassengerShifts, b: IPassengerShifts, direction: 1 | -1): number => {
    const codeA = String(a.internalPassCode ?? '').trim();
    const codeB = String(b.internalPassCode ?? '').trim();

    if (!codeA && !codeB) return 0;
    if (!codeA) return 1;
    if (!codeB) return -1;

    return direction * compareCodes(codeA, codeB);
};

/**
 * The passengers in the order of the employee column's sort menu; the same array when there is no sort.
 * Same comparisons as the table always made (string <, never 0), on a copy.
 */
const sortPassengers = (data: IPassengerShifts[], selectedSort?: string | null): IPassengerShifts[] => {
    if (!selectedSort) return data;

    const sort = String(selectedSort);
    const copy = [...data];

    copy.sort((a, b) => {
        if (sort === SORT.CityAsc) return a.city < b.city ? -1 : 1;
        if (sort === SORT.CityDesc) return b.city < a.city ? -1 : 1;
        if (sort === SORT.NameAsc) return a.fullName < b.fullName ? -1 : 1;
        if (sort === SORT.NameDesc) return b.fullName < a.fullName ? -1 : 1;
        if (sort === SORT.DepartmentAsc) return a.departmentName < b.departmentName ? -1 : 1;
        if (sort === SORT.CodeAsc) return compareByCode(a, b, 1);
        if (sort === SORT.CodeDesc) return compareByCode(a, b, -1);
        // Only value left is DepartmentDesc
        return b.departmentName < a.departmentName ? -1 : 1;
    });

    return copy;
};

/** Each passenger with isSelected, looked up in a Set: O(passengers) instead of passengers x selected */
const markSelected = (data: IPassengerShifts[], selectedIds: string[]): IPassengerShifts[] => {
    const selected = new Set(selectedIds);
    return data.map((passenger) => ({ ...passenger, isSelected: selected.has(passenger.passId) }));
};

/**
 * Selected passengers first, each group keeping its incoming (already sorted) order.
 * Returns the same array when nothing is selected. One Set lookup per passenger: O(passengers).
 */
const pinSelected = (data: IPassengerShifts[], selectedIds: string[]): IPassengerShifts[] => {
    if (selectedIds.length === 0) return data;

    const selected = new Set(selectedIds);
    const pinned: IPassengerShifts[] = [];
    const others: IPassengerShifts[] = [];

    data.forEach((passenger) => {
        if (selected.has(passenger.passId)) pinned.push(passenger);
        else others.push(passenger);
    });

    return pinned.length === 0 ? data : [...pinned, ...others];
};

/**
 * The passengers that pass `matches`, plus every selected passenger even when `matches` rejects it.
 * Original order is kept (pinSelected moves the selected ones up later). `matches` is injected so this
 * file stays free of the search helpers' imports. O(passengers).
 */
const filterKeepingSelected = (
    data: IPassengerShifts[],
    selectedIds: string[],
    matches: (passenger: IPassengerShifts) => boolean
): IPassengerShifts[] => {
    const selected = new Set(selectedIds);
    return data.filter((passenger) => selected.has(passenger.passId) || matches(passenger));
};

const manualOrderTableHelpers = { sortPassengers, markSelected, pinSelected, filterKeepingSelected };

export default manualOrderTableHelpers;
